import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { groupCartProps } from "@/interface/cart";
import toast from "react-hot-toast";

const initialState: groupCartProps = {
	id: "",
	totalPrice: 0,
	allowShopping: null,
	owner: null,
	initialPrice: 0,
	items: [],
	discount: null,
	invitees: [],
	loading: false, // Add loading to initial state
};

const groupCartSlice = createSlice({
	name: "group_cart",
	initialState,
	reducers: {
		setGroupCartLoading: (state, action: PayloadAction<boolean>) => {
			state.loading = action.payload;
		},
		createGroupCart: (state, action: PayloadAction<groupCartProps>) => {
			return {
				...state,
				id: action.payload.id,
				totalPrice: action.payload.totalPrice,
				allowShopping: action.payload.allowShopping,
				owner: action.payload.owner,
				items: action.payload.items,
				discount: action.payload.discount,
				invitees: action.payload.invitees,
			};
		},
		updateGroupCartDetails(state, action: PayloadAction<groupCartProps>) {
			state.id = action.payload.id;
			state.totalPrice = action.payload.totalPrice;
			state.allowShopping = action.payload.allowShopping;
			state.owner = action.payload.owner;
			// Other fields remain unchanged.
		},
		updateGroupCartInvitees(state, action: PayloadAction<groupCartProps>) {
			state.invitees = action.payload.invitees;
		},
		addToGroupCart: (state, action: PayloadAction<groupCartProps>) => {
			const newItems = action.payload.items || [];
			const prevItems = state.items || [];

			if (newItems.length > prevItems.length) {
				const newItem = newItems.find(
					(newItem: any) =>
						!prevItems.some((prevItem: any) => prevItem.id === newItem.id)
				);
				if (newItem) {
					toast.remove();
					toast.success(
						`${newItem.product.name} was successfully added to your group cart`
					);
				}
			} else {
				const updatedItem = newItems.find((newItem: any) =>
					prevItems.some(
						(prevItem: any) =>
							prevItem.id === newItem.id &&
							newItem.quantity > prevItem.quantity
					)
				);
				if (updatedItem) {
					toast.remove();
					toast.success(
						`You successfully added another ${updatedItem.product.name} to your group cart`
					);
				}
			}

			return {
				...state,
				id: action.payload.id,
				totalPrice: action.payload.totalPrice,
				allowShopping: action.payload.allowShopping,
				invitees: action.payload.invitees,
				items: newItems,
			};
		},
		deleteGroupCartProduct: (state, action: PayloadAction<groupCartProps>) => {
			return {
				...state,
				id: action.payload.id,
				totalPrice: action.payload.totalPrice,
				items: action.payload.items,
			};
		},
		incrementProductQuantity: (state, action: PayloadAction<any>) => {
			if (state.id === action.payload.cartId) {
				const item = state.items?.find(
					(item: any) => item.product.id === action.payload.productId
				);
				if (item) {
					item.quantity += 1;
				}
			}
			state.totalPrice = action.payload.totalPrice;
			state.items = action.payload.items;
		},
		decrementProductQuantity: (state, action: PayloadAction<any>) => {
			const { cartId, productId, items, totalPrice } = action.payload;
			if (state?.id === cartId) {
				const item = state.items?.find(
					(item: any) => item.product.id === productId
				);
				if (item && item.quantity > 1) {
					item.quantity -= 1;
				}
			}
			state.totalPrice = totalPrice;
			state.items = items;
		},
	},
});

export default groupCartSlice.reducer;
export const {
	createGroupCart,
	addToGroupCart,
	deleteGroupCartProduct,
	incrementProductQuantity,
	decrementProductQuantity,
	updateGroupCartDetails,
	updateGroupCartInvitees,
	setGroupCartLoading,
} = groupCartSlice.actions;
